"use client";

/**
 * Group of (styled) components used for pop-up menus.
 *
 * Usage:
 * <ToggleBox close={() => {this.closeMenu()}}>
 *  <MenuHeader>First Section</MenuHeader>
 *  <MenuItem>
 *    <Link to="/first-thing/">First Thing</Link>
 *  <MenuItem>
 * </ToggleBox>
 *
 */
import React, { ComponentProps, Fragment } from "react";
import styled from "styled-components";

import { SpacingValue } from "../ComponentProps";

import Icon from "../Icon";
import Flex from "../Flex";
import Box, { BoxProps } from "../Box";
import Text from "../Text";
import Switch from "../Switch";
import Button from "../Button";
import { Badge } from "../Badge";
import { cssVal } from "../utils";

type DescriptionButton = {
  text: string;
  onClick: () => void;
};

type Props = {
  title: string;
  id?: string;
  icon?: string;
  description?: string | React.ReactNode;
  isOpen?: boolean;
  isOn?: boolean;
  disabled?: boolean;
  // As of 7/31/21 Eslint crashes if this is defined as a function
  onToggle?: (isOn: boolean) => void;
  estimatedHeight?: SpacingValue;
  children?: React.ReactNode;
  styleOverrides?: BoxProps;
  control?: "none" | "switch" | "button";
  buttonProps?: React.ComponentProps<typeof Button>;
  descriptionButton?: DescriptionButton;
  titleBadge?: string | ComponentProps<typeof Badge>;
  action?: React.ReactNode;
  customControl?: React.ReactNode | string;
} & BoxProps;

type ContentBoxProps = Pick<Props, "isOpen" | "estimatedHeight">;
const ContentBox = styled(Box)<ContentBoxProps>`
  transition:
    opacity 500ms,
    padding-top 500ms,
    padding-bottom 500ms,
    max-height 500ms;
  opacity: ${props => (props.isOpen ? 1 : 0)};
  max-height: ${props => (props.isOpen ? cssVal(props.estimatedHeight || "200px") : 0)};
`;

const ToggleBox: React.FC<Props> = ({
  id,
  icon,
  title,
  description,
  isOpen,
  isOn,
  disabled,
  onToggle,
  children,
  estimatedHeight,
  control = "switch",
  styleOverrides,
  descriptionButton,
  buttonProps,
  titleBadge,
  action,
  customControl,
}) => {
  return (
    <Box
      data-testid={id}
      borderRadius="default"
      border="default"
      background="grayLightest"
      {...styleOverrides}>
      <Flex p="m" justifyContent="space-between" alignItems="center" gap="40px">
        <Flex flex={5} flexDirection="column" pr="8px">
          <Flex alignItems="center">
            {icon && <Icon pr="s" iconId={icon} />}
            <Text id="toggle-box-title" font="ui.regularBold" mr="s">
              {title}
            </Text>
            {typeof titleBadge === "string" && <Badge id={`${title}-upgrade`} text={titleBadge} />}
            {typeof titleBadge === "object" && titleBadge?.text && (
              <Badge id={`${title}-upgrade`} {...titleBadge} />
            )}
          </Flex>
          {description && (
            <Text id="toggle-box-description" mt="xs" m={0}>
              {description}
              {descriptionButton && (
                <>
                  {" "}
                  <Button
                    onClick={descriptionButton.onClick}
                    text={descriptionButton.text}
                    buttonStyle="tertiary"
                  />
                </>
              )}
            </Text>
          )}
        </Flex>
        {action}
        {control === "switch" && !customControl && (
          <div className="toggle">
            <Switch
              id="toggle-box-switch"
              isOn={typeof isOn === "boolean" ? isOn : isOpen}
              disabled={disabled}
              size="small"
              onToggle={(toggledOn: boolean) => {
                onToggle(toggledOn);
              }}
            />
          </div>
        )}
        {control === "button" && !customControl && <Button disabled={disabled} {...buttonProps} />}
        {customControl && <Fragment>{customControl}</Fragment>}
      </Flex>
      {children && (
        <ContentBox
          data-testid="toggle-box-content"
          isOpen={isOpen}
          estimatedHeight={estimatedHeight}
          py={isOpen ? "m" : "0"}
          px="m"
          borderTop="default"
          overflow="hidden">
          {children}
        </ContentBox>
      )}
    </Box>
  );
};

export default ToggleBox;
